import { Suggestion, RiskLevel, SuggestionStatus } from '../types/mockup'
import { ImpactAnalysis, NotificationPreview, DismissalConsequence, SuggestionGroup } from '../types/suggestions'

// Helper function to create dates relative to event time
const eventDate = new Date('2025-12-15T10:00:00') // Future date for demo
const createTime = (hoursOffset: number, minutesOffset: number = 0) => {
  const date = new Date(eventDate)
  date.setHours(date.getHours() + hoursOffset)
  date.setMinutes(date.getMinutes() + minutesOffset)
  return date
}

export const mockSuggestions: Suggestion[] = [
  {
    id: 'sug-001',
    type: 'schedule_change',
    title: 'Catering Delay Impact',
    headline: 'Catering delayed by 30 minutes due to traffic',
    description: 'Elite Catering is running late due to traffic. Recommend delaying setup to prevent venue congestion.',
    understanding: 'high',
    confidence: 0.92,
    riskLevel: 'critical' as RiskLevel,
    source: 'sms',
    sourceMessageId: 'msg-123',
    createdAt: new Date(),
    expiresAt: createTime(0, 30),
    status: 'pending' as SuggestionStatus,
    impact: {
      tasksAffected: ['task-catering-setup', 'task-cocktail-hour', 'task-photo-session'],
      suppliersAffected: ['elite-catering', 'bar-services', 'photography'],
      guestsAffected: 150,
      minutesImpact: 15,
      cascadingChanges: ['Cocktail hour compressed', 'Photo timeline adjusted', 'Bar service extended']
    },
    operationalImpact: {
      affectedTasks: ['task-catering-setup', 'task-cocktail-hour'],
      affectedVendors: ['elite-catering', 'bar-services'],
      timelineChanges: [
        {
          taskId: 'task-catering-setup',
          oldTime: createTime(3, 0),
          newTime: createTime(3, 15)
        },
        {
          taskId: 'task-cocktail-hour',
          oldTime: createTime(4, 0),
          newTime: createTime(4, 15)
        }
      ],
      financialImpact: 200
    },
    experienceImpact: {
      guestSatisfactionDelta: -12,
      emotionalArc: [
        'Anticipation during ceremony',
        'Confusion about where to go',
        'Hunger during photo session',
        'Relief when cocktail hour begins'
      ],
      riskDescription: 'Guests likely to notice delay and become hungry during photos',
      probabilityOfNotice: 78
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'elite-catering',
          recipientType: 'supplier',
          channel: 'sms',
          message: 'Setup time adjusted to 3:15 PM due to venue availability. Please confirm.',
          priority: 'urgent',
          scheduledFor: new Date()
        },
        {
          recipientId: 'bar-services',
          recipientType: 'supplier',
          channel: 'sms',
          message: 'Extended service needed 3:00-3:30 PM for early arrivals. Additional compensation confirmed.',
          priority: 'urgent',
          scheduledFor: new Date()
        }
      ]
    },
    alternativeSuggestions: [
      'Start ceremony 10 minutes earlier',
      'Serve pre-ceremony refreshments',
      'Extend cocktail hour by 10 minutes'
    ],
    dismissalConsequences: [
      '90% chance of setup collision at 3:00 PM',
      'Venue staff confusion and stress',
      'Guest complaints about service gaps'
    ]
  },
  {
    id: 'sug-002',
    type: 'alert',
    title: 'Florist Wrong Location Alert',
    headline: 'Florist heading to wrong venue',
    description: 'Bloom Florals GPS shows movement away from venue. They may be heading to previous venue address.',
    understanding: 'high',
    confidence: 0.95,
    riskLevel: 'critical' as RiskLevel,
    source: 'location',
    createdAt: new Date(),
    expiresAt: createTime(0, 15),
    status: 'pending' as SuggestionStatus,
    impact: {
      tasksAffected: ['task-floral-setup', 'task-ceremony-decor'],
      suppliersAffected: ['bloom-florals'],
      guestsAffected: 150,
      minutesImpact: 45,
      cascadingChanges: ['Ceremony decor incomplete', 'Photo backdrop missing', 'Guest arrival to unfinished venue']
    },
    operationalImpact: {
      affectedTasks: ['task-floral-setup'],
      affectedVendors: ['bloom-florals'],
      timelineChanges: [
        {
          taskId: 'task-floral-setup',
          oldTime: createTime(2, 0),
          newTime: createTime(2, 45)
        }
      ],
      financialImpact: 0
    },
    experienceImpact: {
      guestSatisfactionDelta: -25,
      emotionalArc: [
        'Disappointment at incomplete decor',
        'Distraction during ceremony',
        'Reduced photo quality'
      ],
      riskDescription: 'First impressions severely impacted, ceremony aesthetics compromised',
      probabilityOfNotice: 95
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'bloom-florals',
          recipientType: 'supplier',
          channel: 'sms',
          message: 'URGENT: You appear to be heading to wrong location. Correct venue: Grand Ballroom, 123 Main St. Reply HELP for directions.',
          priority: 'urgent',
          scheduledFor: new Date()
        }
      ]
    },
    alternativeSuggestions: [
      'Send venue staff to redirect at likely wrong location',
      'Prepare backup decoration options',
      'Delay guest arrival by 15 minutes'
    ],
    dismissalConsequences: [
      'Florist arrives at wrong venue, 45+ minute delay',
      'No ceremony flowers for photos',
      'Significant guest disappointment'
    ]
  },
  {
    id: 'sug-003',
    type: 'optimization',
    title: 'Timeline Optimization',
    headline: 'Compress photo session by 10 minutes',
    description: 'Analysis shows photo session can be shortened without quality impact, creating buffer for dinner service.',
    understanding: 'medium',
    confidence: 0.75,
    riskLevel: 'warning' as RiskLevel,
    source: 'system',
    createdAt: new Date(),
    expiresAt: createTime(1, 0),
    status: 'pending' as SuggestionStatus,
    impact: {
      tasksAffected: ['task-photo-session', 'task-dinner-service'],
      suppliersAffected: ['photography', 'elite-catering'],
      guestsAffected: 30,
      minutesImpact: -10,
      cascadingChanges: ['More relaxed dinner transition', 'Additional cocktail time']
    },
    operationalImpact: {
      affectedTasks: ['task-photo-session', 'task-dinner-service'],
      affectedVendors: ['photography'],
      timelineChanges: [
        {
          taskId: 'task-photo-session',
          oldTime: createTime(5, 0),
          newTime: createTime(4, 50)
        }
      ],
      financialImpact: 0
    },
    experienceImpact: {
      guestSatisfactionDelta: 8,
      emotionalArc: [
        'Less waiting during photos',
        'More mingling time',
        'Smoother dinner transition'
      ],
      riskDescription: 'Slight rush in photos but better overall flow',
      probabilityOfNotice: 20
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'photography',
          recipientType: 'supplier',
          channel: 'email',
          message: 'Photo session optimized to 50 minutes. Focus on priority shots list.',
          priority: 'normal',
          scheduledFor: new Date()
        }
      ]
    }
  },
  {
    id: 'sug-004',
    type: 'notification',
    title: 'Band Setup Reminder',
    headline: 'Send setup reminder to band',
    description: 'Band has not confirmed arrival. Send reminder 2 hours before performance.',
    understanding: 'high',
    confidence: 0.88,
    riskLevel: 'info' as RiskLevel,
    source: 'system',
    createdAt: new Date(),
    expiresAt: createTime(2, 0),
    impact: {
      tasksAffected: ['task-band-setup'],
      suppliersAffected: ['jazz-quartet'],
      minutesImpact: 0,
      cascadingChanges: []
    },
    operationalImpact: {
      affectedTasks: ['task-band-setup'],
      affectedVendors: ['jazz-quartet'],
      timelineChanges: [],
      financialImpact: 0
    },
    experienceImpact: {
      guestSatisfactionDelta: 0,
      emotionalArc: [],
      riskDescription: 'Preventive measure to ensure timely setup',
      probabilityOfNotice: 0
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'jazz-quartet',
          recipientType: 'supplier',
          channel: 'sms',
          message: 'Reminder: Your performance at Grand Ballroom starts at 7:00 PM. Setup from 6:00 PM. Please confirm.',
          priority: 'normal',
          scheduledFor: createTime(5, 0)
        }
      ]
    },
    status: 'pending' as SuggestionStatus
  },
  {
    id: 'sug-005',
    type: 'schedule_change',
    title: 'Weather Contingency',
    headline: 'Move ceremony indoors due to weather',
    description: '70% chance of rain at ceremony time. Recommend moving to indoor backup location.',
    understanding: 'high',
    confidence: 0.70,
    riskLevel: 'warning' as RiskLevel,
    source: 'weather-api',
    createdAt: new Date(),
    expiresAt: createTime(1, 30),
    impact: {
      tasksAffected: ['task-ceremony', 'task-ceremony-setup', 'task-guest-seating'],
      suppliersAffected: ['venue-staff', 'bloom-florals', 'av-team'],
      guestsAffected: 150,
      minutesImpact: 30,
      cascadingChanges: ['Decor relocation', 'Seating rearrangement', 'Audio setup change']
    },
    operationalImpact: {
      affectedTasks: ['task-ceremony', 'task-ceremony-setup'],
      affectedVendors: ['venue-staff', 'bloom-florals', 'av-team'],
      timelineChanges: [
        {
          taskId: 'task-ceremony-setup',
          oldTime: createTime(2, 0),
          newTime: createTime(1, 30)
        }
      ],
      financialImpact: 500
    },
    experienceImpact: {
      guestSatisfactionDelta: -5,
      emotionalArc: [
        'Disappointment about outdoor setting',
        'Appreciation for staying dry',
        'Intimate indoor atmosphere'
      ],
      riskDescription: 'Loss of garden views but comfortable guest experience',
      probabilityOfNotice: 100
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'all-vendors',
          recipientType: 'team',
          channel: 'both',
          message: 'WEATHER UPDATE: Ceremony moving indoors to Grand Ballroom. Setup begins 1:30 PM.',
          priority: 'urgent',
          scheduledFor: new Date()
        }
      ]
    },
    alternativeSuggestions: [
      'Wait 1 hour for updated forecast',
      'Prepare both locations simultaneously',
      'Use covered outdoor pavilion'
    ],
    dismissalConsequences: [
      '70% chance guests get wet',
      'Potential ceremony delay if rain starts',
      'Guest discomfort and complaints'
    ],
    status: 'pending' as SuggestionStatus
  },
  {
    id: 'sug-006',
    type: 'alert',
    title: 'Kitchen Equipment Issue',
    headline: 'Kitchen oven malfunction detected',
    description: 'Venue reports main oven not reaching temperature. May affect dinner service timing.',
    understanding: 'high',
    confidence: 0.85,
    riskLevel: 'critical' as RiskLevel,
    source: 'venue',
    createdAt: new Date(),
    expiresAt: createTime(0, 20),
    impact: {
      tasksAffected: ['task-dinner-prep', 'task-dinner-service'],
      suppliersAffected: ['elite-catering', 'venue-staff'],
      guestsAffected: 150,
      minutesImpact: 20,
      cascadingChanges: ['Dinner delay', 'Extended cocktail hour', 'Speech timing adjustment']
    },
    operationalImpact: {
      affectedTasks: ['task-dinner-service'],
      affectedVendors: ['elite-catering'],
      timelineChanges: [
        {
          taskId: 'task-dinner-service',
          oldTime: createTime(6, 0),
          newTime: createTime(6, 20)
        }
      ],
      financialImpact: 300
    },
    experienceImpact: {
      guestSatisfactionDelta: -15,
      emotionalArc: [
        'Extended cocktail enjoyment',
        'Slight hunger increase',
        'Concern about delays'
      ],
      riskDescription: 'Noticeable dinner delay but manageable with communication',
      probabilityOfNotice: 60
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'elite-catering',
          recipientType: 'supplier',
          channel: 'sms',
          message: 'URGENT: Main oven issue. Prepare contingency menu items using stovetop/secondary ovens.',
          priority: 'urgent',
          scheduledFor: new Date()
        }
      ]
    },
    alternativeSuggestions: [
      'Modify menu to cold options',
      'Use backup catering equipment',
      'Stagger course service'
    ],
    status: 'pending' as SuggestionStatus,
    dismissalConsequences: [
      'Significant dinner delay (30+ minutes)',
      'Cold food service',
      'Major guest dissatisfaction'
    ]
  },
  {
    id: 'sug-007',
    type: 'optimization',
    title: 'Supplier Coordination',
    headline: 'Batch supplier notifications for efficiency',
    description: 'Multiple small timeline changes can be communicated together to reduce message fatigue.',
    understanding: 'high',
    confidence: 0.82,
    riskLevel: 'info' as RiskLevel,
    source: 'system',
    createdAt: new Date(),
    expiresAt: createTime(3, 0),
    impact: {
      tasksAffected: [],
      suppliersAffected: ['all-vendors'],
      minutesImpact: 0,
      cascadingChanges: []
    },
    operationalImpact: {
      affectedTasks: [],
      affectedVendors: ['all-vendors'],
      timelineChanges: [],
      financialImpact: 0
    },
    experienceImpact: {
      guestSatisfactionDelta: 0,
      emotionalArc: [],
      riskDescription: 'Improved vendor coordination and reduced confusion',
      probabilityOfNotice: 0
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'all-vendors',
          recipientType: 'team',
          channel: 'email',
          message: 'Timeline Update Summary: [Batched changes listed here]',
          priority: 'normal',
          scheduledFor: createTime(1, 0)
        }
      ]
    },
    status: 'pending' as SuggestionStatus
  },
  {
    id: 'sug-008',
    type: 'assignment',
    title: 'Staff Reallocation',
    headline: 'Reassign idle staff to assist with setup',
    description: 'Bar staff idle during ceremony can help with reception setup.',
    understanding: 'medium',
    confidence: 0.68,
    riskLevel: 'info' as RiskLevel,
    source: 'system',
    createdAt: new Date(),
    expiresAt: createTime(2, 0),
    impact: {
      tasksAffected: ['task-reception-setup'],
      suppliersAffected: ['bar-services', 'venue-staff'],
      minutesImpact: -15,
      cascadingChanges: ['Faster setup completion']
    },
    operationalImpact: {
      affectedTasks: ['task-reception-setup'],
      affectedVendors: ['bar-services'],
      timelineChanges: [],
      financialImpact: 0
    },
    experienceImpact: {
      guestSatisfactionDelta: 5,
      emotionalArc: ['Smoother transition to reception'],
      riskDescription: 'More efficient use of resources',
      probabilityOfNotice: 10
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'bar-services',
          recipientType: 'supplier',
          channel: 'sms',
          message: 'Please assist with reception setup during ceremony (3:00-3:45 PM). Regular duties resume at 4:00 PM.',
          priority: 'normal',
          scheduledFor: createTime(2, 30)
        }
      ]
    },
    status: 'pending' as SuggestionStatus
  },
  {
    id: 'sug-009',
    type: 'alert',
    title: 'Compliance Alert',
    headline: 'AV team insurance expired',
    description: 'ProSound AV insurance certificate expired yesterday. Venue requires valid coverage.',
    understanding: 'high',
    confidence: 0.99,
    riskLevel: 'critical' as RiskLevel,
    source: 'compliance-check',
    createdAt: new Date(),
    expiresAt: createTime(0, 30),
    impact: {
      tasksAffected: ['task-av-setup', 'task-ceremony-audio', 'task-reception-audio'],
      suppliersAffected: ['prosound-av'],
      guestsAffected: 150,
      minutesImpact: 0,
      cascadingChanges: ['Potential vendor rejection', 'No audio/visual support']
    },
    operationalImpact: {
      affectedTasks: ['task-av-setup'],
      affectedVendors: ['prosound-av'],
      timelineChanges: [],
      financialImpact: 2000
    },
    experienceImpact: {
      guestSatisfactionDelta: -50,
      emotionalArc: [
        'No ceremony audio',
        'No reception music',
        'No speeches amplification'
      ],
      riskDescription: 'Event audio/visual completely compromised',
      probabilityOfNotice: 100
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'prosound-av',
          recipientType: 'supplier',
          channel: 'both',
          message: 'URGENT: Insurance certificate expired. Provide updated documentation immediately or access will be denied.',
          priority: 'urgent',
          scheduledFor: new Date()
        }
      ]
    },
    alternativeSuggestions: [
      'Contact backup AV provider',
      'Use venue house system',
      'Request insurance waiver from venue'
    ],
    dismissalConsequences: [
      'Vendor barred from venue',
      'No AV support for event',
      'Legal liability for venue'
    ],
    status: 'pending' as SuggestionStatus
  },
  {
    id: 'sug-010',
    type: 'schedule_change',
    title: 'Guest Count Increase',
    headline: 'Adjust catering for 20 additional guests',
    description: 'Last-minute RSVPs increased count. Catering needs adjustment.',
    understanding: 'high',
    confidence: 0.90,
    riskLevel: 'warning' as RiskLevel,
    source: 'manual',
    createdAt: new Date(),
    expiresAt: createTime(1, 0),
    status: 'pending' as SuggestionStatus,
    impact: {
      tasksAffected: ['task-catering-prep', 'task-dinner-service'],
      suppliersAffected: ['elite-catering'],
      guestsAffected: 20,
      minutesImpact: 0,
      cascadingChanges: ['Additional food prep', 'Seating adjustment']
    },
    operationalImpact: {
      affectedTasks: ['task-catering-prep'],
      affectedVendors: ['elite-catering'],
      timelineChanges: [],
      financialImpact: 800
    },
    experienceImpact: {
      guestSatisfactionDelta: -8,
      emotionalArc: [
        'Potential food shortage',
        'Crowded seating',
        'Reduced portion sizes'
      ],
      riskDescription: 'Risk of insufficient food if not addressed',
      probabilityOfNotice: 40
    },
    proposedChanges: {
      notifications: [
        {
          recipientId: 'elite-catering',
          recipientType: 'supplier',
          channel: 'sms',
          message: 'Guest count increased to 170 (+20). Please confirm additional servings available.',
          priority: 'urgent',
          scheduledFor: new Date()
        }
      ]
    }
  }
]

console.log('Mock suggestions loaded:', mockSuggestions.length, 'items')

// Mock suggestion groups for related suggestions
export const mockSuggestionGroups: SuggestionGroup[] = [
  {
    id: 'group-001',
    title: 'Catering and Service Adjustments',
    description: 'Related changes to food service timeline that work better together',
    suggestions: ['sug-001', 'sug-003', 'sug-006'],
    combinedImpact: {
      totalMinutesChange: 25,
      totalVendorsAffected: 4,
      totalGuestsAffected: 150,
      overallRiskLevel: 'critical' as RiskLevel
    },
    synergyScore: 85,
    recommendedOrder: ['sug-006', 'sug-001', 'sug-003']
  },
  {
    id: 'group-002',
    title: 'Vendor Coordination Bundle',
    description: 'Supplier notifications that should be sent together',
    suggestions: ['sug-004', 'sug-007', 'sug-008'],
    combinedImpact: {
      totalMinutesChange: -15,
      totalVendorsAffected: 5,
      totalGuestsAffected: 0,
      overallRiskLevel: 'info' as RiskLevel
    },
    synergyScore: 72,
    recommendedOrder: ['sug-007', 'sug-004', 'sug-008']
  }
]

// Mock dismissal consequences for demonstration
export const mockDismissalConsequences: Record<string, DismissalConsequence[]> = {
  'sug-001': [
    {
      severity: 'high',
      description: 'Setup collision at 3:00 PM',
      probability: 90,
      manualWorkRequired: [
        'On-site coordination between vendors',
        'Manual traffic control in service areas',
        'Real-time schedule adjustments'
      ],
      stressLevel: 'high',
      alternativeActions: ['Assign dedicated coordinator to manage arrivals']
    },
    {
      severity: 'medium',
      description: 'Guest confusion during transition',
      probability: 75,
      manualWorkRequired: [
        'Additional signage needed',
        'Staff to direct guests'
      ],
      stressLevel: 'moderate'
    }
  ],
  'sug-002': [
    {
      severity: 'critical',
      description: 'No ceremony flowers',
      probability: 95,
      manualWorkRequired: [
        'Emergency florist sourcing',
        'Venue decoration improvisation',
        'Guest expectation management'
      ],
      stressLevel: 'extreme',
      alternativeActions: ['Send staff to intercept at wrong venue']
    }
  ]
}

// Mock notification previews
export const mockNotificationPreviews: NotificationPreview[] = [
  {
    id: 'notif-001',
    recipientId: 'elite-catering',
    recipientName: 'Elite Catering Co.',
    recipientType: 'supplier',
    channel: 'sms',
    template: {
      recipientId: 'elite-catering',
      recipientType: 'supplier',
      channel: 'sms',
      message: 'Setup time adjusted to 3:15 PM due to venue availability. Please confirm.',
      priority: 'urgent',
      scheduledFor: new Date()
    },
    estimatedDeliveryTime: new Date(),
    isEditable: true,
    deliveryStatus: 'pending'
  },
  {
    id: 'notif-002',
    recipientId: 'bar-services',
    recipientName: 'Premium Bar Services',
    recipientType: 'supplier',
    channel: 'sms',
    template: {
      recipientId: 'bar-services',
      recipientType: 'supplier',
      channel: 'sms',
      message: 'Extended service needed 3:00-3:30 PM. Additional compensation confirmed.',
      priority: 'urgent',
      scheduledFor: new Date()
    },
    estimatedDeliveryTime: new Date(),
    isEditable: true,
    deliveryStatus: 'pending'
  }
]